import * as api from '@/api/api'
import * as types from './types'
import * as mutations from '../mutations/types'
import Notification from '@/utils/notification'
import { router } from '@/router/router'
import { exitFullscreen } from '@/utils/common'

const updateNow = ({ commit }, now) => {
  commit(mutations.MUTATE_NOW, now)
}

const setAccount = ({ commit }, body) => {
  commit(mutations.MUTATE_ACCOUNT, body)
}

const logout = async ({ commit }) => {
  try {
    const { account, _to } = await api.logout()
    localStorage.setItem('listener-account', account.id)
    commit(mutations.MUTATE_ACCOUNT, account)
    location.replace(_to)
  } catch {}
}

const getAccount = async ({ commit }) => {
  try {
    const { account, vendor, _build } = await api.getAccount()
    localStorage.setItem('listener-account', account.id)
    commit(mutations.MUTATE_ACCOUNT, account)
    commit(mutations.MUTATE_VENDOR, vendor)
    commit(mutations.MUTATE_BUILD, _build)
  } catch {}
}

const register = async (_, body) => {
  try {
    const { _to } = await api.register(body)
    setTimeout(() => location.replace(_to), 3 * 1000)
  } catch {}
}

const login = async ({ commit, dispatch }, { token, body = new FormData() }) => {
  try {
    commit(mutations.MUTATE_USERNAME, body.get('username'))

    const { account, _to } = await api.login(token, body)

    localStorage.setItem('listener-account', account.id)
    setAccount({ commit }, account)

    // Отправим аутентифицированного пользователя по ссылке из СДО.
    // По умолчанию отправляем на главную страницу.
    router.push(_to || (account.id && '/') || undefined).catch(() => {})
  } catch {}
}

const reset = async (_, body) => {
  try {
    const { _to } = await api.resetPassword(body)
    router.push(_to).catch(() => {})
  } catch {}
}

const createNotification = ({ dispatch, commit }, notification) => {
  if (!notification.msg) { return }

  dispatch(types.DELETE_NOTIFICATIONS)
  commit(
    mutations.MUTATE_NOTIFICATIONS,
    [new Notification(notification, () => dispatch(types.DELETE_NOTIFICATION))]
  )
}

const createNotifications = ({ dispatch, commit }, notifications) => {
  if (!notifications.length) { return }
  dispatch(types.DELETE_NOTIFICATIONS)
  commit(
    mutations.MUTATE_NOTIFICATIONS,
    notifications
      .filter(notification => notification.msg)
      .map(notification => {
        return new Notification(notification, () => dispatch(types.DELETE_NOTIFICATION))
      })
  )
}

const deleteNotification = ({ state, commit }, notification) => {
  const notifications = state.notifications
  if (notifications.length && notification?.msg) {
    const idx = notifications.findIndex(n => n.msg === notification.msg)

    if (idx !== -1) {
      notifications[idx].destroy()
      notifications.splice(idx, 1)
      commit(mutations.MUTATE_NOTIFICATIONS, notifications)
    }
  }
}

const deleteNotifications = ({ state, commit }) => {
  if (state.notifications.length) {
    state.notifications.forEach(notification => {
      notification.destroy()
    })
    commit(mutations.MUTATE_NOTIFICATIONS)
  }
}

const updateCountNewMessages = ({ commit }, val) => {
  commit(mutations.MUTATE_COUNT_NEW_MESSAGES, val)
}

const getSessions = async ({ commit }, silent) => {
  const { sessions, now } = await api.getSessions(silent)

  commit(mutations.MUTATE_SESSIONS, sessions || [])
  commit(mutations.MUTATE_NOW, now)
}

const getNews = async ({ commit }, { silent, query }) => {
  const { news, pager, now } = await api.getNews(silent, query)

  commit(mutations.MUTATE_NEWS, news || [])
  commit(mutations.MUTATE_NOW, now)

  if (query) {
    commit(mutations.MUTATE_PAGER, pager)
  }
}

const getCountNewNews = async ({ state, commit }, silent) => {
  const user = state.account.id
  const { count, now } = await api.getUnreadNewsCount(user, silent)

  commit(mutations.MUTATE_COUNT_NEW_NEWS, count || 0)
  commit(mutations.MUTATE_NOW, now)
}

const continueSession = async ({ commit }, sessionId) => {
  const { session, step } = await api.continueSession(sessionId)
  commit(mutations.MUTATE_SESSIONS_ITEM, session)

  return { session, step }
}

const startSession = async ({ commit }, { sessionId, data }) => {
  const { session, step } = await api.startSession(sessionId, data)
  commit(mutations.MUTATE_SESSIONS_ITEM, session)

  return { session, step }
}

const toggleSteps = ({ commit }, value = false) => {
  localStorage.setItem('isOpenSteps', Boolean(value))
  commit(mutations.MUTATE_TOGGLE_STEPS, Boolean(value))
}

const toggleView = ({ commit }, value = false) => {
  localStorage.setItem('isFullView', Boolean(value))
  commit(mutations.MUTATE_TOGGLE_VIEW, Boolean(value))
}

const updateStep = ({ commit, state }, payload) => {
  const {
    permissions = state.permissions,
    max_filesize: maxFilesize = state.maxFilesize,
    now = state.now,
    step = state.step,
    lesson = state.lesson,
    quiz = state.quiz,
    essay = state.essay,
    lecture = state.lecture,
    session = state.session
  } = payload
  // Обновим данные шага в списке шагов сессии (возможно они не обновились сервере)
  const currentStep = (session?.steps || []).find(_step => _step.id === step?.id)
  if (currentStep) {
    Object.assign(currentStep, step, {
      // Поля курса обновлять не будем, так как они могут указывать на родительский курс, а не на текущий
      course_id: currentStep.course_id,
      course_uuid: currentStep.course_uuid,
      course_title: currentStep.course_title
    })
  }

  // Обновим данные для прокторинга теста
  if (quiz) {
    quiz.proctoring_settings = Object.assign({
      proctoredu_url: '',
      proctoredu_token: '',
      examusnet_url: '',
      examusnet_token: '',
    }, payload.proctoring_settings || {})
  }

  commit(mutations.MUTATE_PERMISSIONS, permissions)
  commit(mutations.MUTATE_MAX_FILESIZE, maxFilesize)
  commit(mutations.MUTATE_NOW, now)
  commit(mutations.MUTATE_STEP, step)
  commit(mutations.MUTATE_LESSON, lesson)
  commit(mutations.MUTATE_QUIZ, quiz)
  commit(mutations.MUTATE_ESSAY, essay)
  commit(mutations.MUTATE_LECTURE, lecture)
  commit(mutations.MUTATE_SESSION, session)
}

const saveQuizQuestion = async ({ commit }, opts) => {
  const { question, user_answer: userAnswer } = await api.saveQuestion(opts.quizId, opts.questionId, opts.data)
  commit(mutations.SAVE_QUIZ_QUESTION, { question, userAnswer })

  return question
}

const saveScorm = async ({ commit, state }, { stepID, grades, accepted, scormData = null }) => {
  try {
    const data = new FormData()
    data.append('grade', grades.grade)
    data.append('raw_grade', grades.raw || 0)
    data.append('min_grade', grades.min || 0)
    data.append('max_grade', grades.max || 0)
    data.append('status', (accepted && 'accepted') || 'rejected')
    data.append('state', (scormData && JSON.stringify(scormData)) || '')
    const response = await api.updateScorm(stepID, data)

    if (response.step?.id) {
      const { session } = state
      const currentStep = (session?.steps || []).find(_step => _step.id === response.step?.id)
      if (currentStep) {
        // Обновим данные шага в списке шагов сессии (возможно они не обновились сервере)
        Object.assign(currentStep, response.step)
        commit(mutations.MUTATE_SESSION, session)
      }

      const { currentRoute } = router
      if (response.step.id === parseInt(currentRoute.params.stepID)) {
        commit(mutations.MUTATE_PERMISSIONS, response.permissions)
        commit(mutations.MUTATE_NOW, response.now)
        commit(mutations.MUTATE_STEP, response.step)
        commit(mutations.MUTATE_LESSON, response.lesson)

        exitFullscreen()
      }
    } else {
      // Что-то пошло не так, перезагрузим текущую страницу.
      location.reload()
    }

    return response
  } catch {}
}

const getLibrary = async ({ commit }) => {
  try {
    const library = await api.getLibrary()

    commit(mutations.MUTATE_LIBRARY, library)
  } catch {}
}

const getExtendedSearch = async ({ commit }, search) => {
  try {
    const ExtendedSearch = await api.getExtendedSearch(search)

    commit(mutations.MUTATE_EXTENDED_SEARCH, ExtendedSearch)
    commit(mutations.MUTATE_PAGINATION_PAGER, ExtendedSearch)
  } catch {}
}

const getLibrarySettings = async ({ commit }) => {
  try {
    const settings = await api.getLibrarySettings()

    commit(mutations.MUTATE_LIBRARY_SETTINGS, settings)
  } catch {}
}

const getLibraryCatalogs = async ({ commit }) => {
  try {
    const catalogs = await api.getLibraryCatalogs()

    commit(mutations.MUTATE_LIBRARY_CATALOGS, catalogs)
  } catch {}
}

const getLibraryCurrentCatalog = async ({ commit }, catalogID) => {
  try {
    const catalog = await api.getLibraryCurrentCatalog(catalogID)

    commit(mutations.MUTATE_LIBRARY_CURRENT_CATALOG, catalog)
  } catch {}
}

const getLibraryFiles = async ({ commit }, catalogID) => {
  try {
    const catalog = await api.getLibraryFiles(catalogID)

    commit(mutations.MUTATE_LIBRARY_FILES, catalog)
  } catch {}
}

const getCourseBooks = async ({ commit }, courseID) => {
  try {
    const books = await api.getCourseBooks(courseID)

    commit(mutations.MUTATE_COURSE_BOOKS, books)
  } catch {}
}

const getSurveys = async ({ commit }, silent) => {
  const { surveys, now } = await api.getSurveys(silent)

  commit(mutations.MUTATE_SURVEYS, surveys || [])
  commit(mutations.MUTATE_NOW, now)
}

const continueSurvey = async ({ commit }, surveyId) => {
  const { survey, step } = await api.continueSurveys(surveyId)
  commit(mutations.MUTATE_SURVEYS_ITEM, survey)

  return { survey, step }
}

const startSurvey = async ({ commit }, data) => {
  const { survey } = await api.startSurvey(data)
  commit(mutations.MUTATE_SURVEYS_ITEM, survey)

  return { survey }
}

const getMeetings = async ({ commit }, silent) => {
  const { meetings, now } = await api.getMeetings(silent)
  commit(mutations.MUTATE_NOW, now)
  commit(mutations.MUTATE_MEETINGS, meetings || [])
}

const getVendorLimits = async ({ commit }, vendorId) => {
  const { limits = {} } = await api.getVendorLimits(vendorId)
  commit(mutations.MUTATE_VENDOR_LIMITS, limits)
}

const setPager = ({ commit }, value) => {
  commit(mutations.MUTATE_PAGER, value)
}

const clearPager = ({ state, commit }) => {
  const DEFAULT_PAGER = {
    page: 0,
    items_per_page: window.localStorage.getItem('items_per_page') || 24
  }

  commit(mutations.MUTATE_PAGER, DEFAULT_PAGER)
}

const getSignedFiles = async ({ commit }, { query }) => {
  const response = await api.getSignedFiles(query)
  const files = response?.files

  if (files) {
    commit(mutations.MUTATE_SIGNED_FILES, files)
  }
}

const getCountNewSignedFiles = async ({ commit }) => {
  const query = {
    is_signed: false,
  }

  const response = await api.getCountNewSignedFiles(query)
  const count = response?.count
  const now = response?.now

  commit(mutations.MUTATE_COUNT_NEW_SIGNED_FILES, count || 0)
  commit(mutations.MUTATE_NOW, now)
}

export default {
  [types.UPDATE_NOW]: updateNow,
  [types.SET_ACCOUNT]: setAccount,
  [types.LOGOUT]: logout,
  [types.GET_ACCOUNT]: getAccount,
  [types.REGISTER]: register,
  [types.LOGIN]: login,
  [types.RESET_PASSWORD]: reset,
  [types.CREATE_NOTIFICATION]: createNotification,
  [types.CREATE_NOTIFICATIONS]: createNotifications,
  [types.DELETE_NOTIFICATIONS]: deleteNotifications,
  [types.DELETE_NOTIFICATION]: deleteNotification,
  [types.UPDATE_COUNT_NEW_MESSAGES]: updateCountNewMessages,
  [types.GET_SESSIONS]: getSessions,
  [types.CONTINUE_SESSION]: continueSession,
  [types.START_SESSION]: startSession,
  [types.TOGGLE_STEPS]: toggleSteps,
  [types.TOGGLE_VIEW]: toggleView,
  [types.UPDATE_STEP]: updateStep,
  [types.SAVE_QUIZ_QUESTION]: saveQuizQuestion,
  [types.SAVE_SCORM]: saveScorm,
  [types.GET_MEETINGS]: getMeetings,
  [types.GET_VENDOR_LIMITS]: getVendorLimits,
  [types.GET_SURVEYS]: getSurveys,
  [types.CONTINUE_SURVEY]: continueSurvey,
  [types.START_SURVEY]: startSurvey,
  [types.GET_LIBRARY]: getLibrary,
  [types.GET_EXTENDED_SEARCH]: getExtendedSearch,
  [types.GET_LIBRARY_SETTINGS]: getLibrarySettings,
  [types.GET_LIBRARY_CATALOGS]: getLibraryCatalogs,
  [types.GET_LIBRARY_CURRENT_CATALOG]: getLibraryCurrentCatalog,
  [types.GET_LIBRARY_FILES]: getLibraryFiles,
  [types.GET_COURSE_BOOKS]: getCourseBooks,
  [types.GET_NEWS]: getNews,
  [types.GET_COUNT_NEW_NEWS]: getCountNewNews,
  [types.SET_PAGER]: setPager,
  [types.CLEAR_PAGER]: clearPager,
  [types.GET_SIGNED_FILES]: getSignedFiles,
  [types.GET_COUNT_NEW_SIGNED_FILES]: getCountNewSignedFiles
}
